import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { BillFindInterface } from '@zaoblako/shared/modules/document/dtos/bill/bill-find.dto';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import type { PlannedIncomeFindOneQueryInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-find-one-query.dto';
import type { PlannedIncomeFindInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-find.dto';
import {
  PlannedIncomeFormDto,
  type PlannedIncomeFormInterface
} from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-form.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { DocumentBillService } from '@zaoblako/web/core/services/finance/document-bill.service';
import { PlannedIncomeService } from '@zaoblako/web/core/services/finance/planned-income.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ContragentAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/contragent-auto-complete/contragent-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { CompanySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/company-single-select/company-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { Observable } from 'rxjs';
import type { CompanyFindManyQueryInterface } from '@zaoblako/shared/modules/client/dtos/company/company-find-many-query.dto';

@Component({
  standalone: true,
  selector: 'crm-planned-income-form',
  templateUrl: './planned-income-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    DatepickerComponent,
    JoinPipe,
    InputNumberModule,
    FormsModule,
    DirectionSingleSelectComponent,
    ButtonComponent,
    ContragentAutoCompleteComponent,
    SpinnerComponent,
    DealAutoCompleteComponent,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    CheckboxModule,
    DividerModule,
    UserGroupMultiSelectComponent,
    UserMultiSelectComponent,
    UserSingleSelectComponent,
    CurrencySingleSelectComponent,
    CompanySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlannedIncomeFormComponent extends BaseFormComponent<PlannedIncomeFormInterface> implements OnInit {
  public companyDropdownSelect: CompanyFindManyQueryInterface['select'] = ['id', 'name', 'deleted', 'currencies'];

  @Input() public readonly index: number;
  public plannedIncome = <PlannedIncomeFormInterface>{};
  public dto = PlannedIncomeFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public currencies: CurrencyFindInterface[] = [];

  public columns: CrmTableColumn[] = [
    { label: 'Номер', name: 'code', sort: 'code:string' },
    { label: 'Дата', name: 'date', sort: 'date:date' },
    { label: 'Сумма', name: 'amount', sort: 'amount:number' },
    { label: 'БС', name: 'direction', sort: 'direction.name:string' },
    { label: 'БЕ', name: 'unit', sort: 'unit.name:string' },
    { label: 'Филиал', name: 'filial', sort: 'filial.name:string' },
    { label: 'Сделка', name: 'deal', sort: 'deal.name:string' },
    { label: 'Контрагент', name: 'contragent', sort: 'contragent.name:string' },
    { label: 'Компания', name: 'company', sort: 'company.name:string' }
  ];

  public get canReadDeal() {
    return this.accessService.finance.dealRights.canReadDeal;
  }

  public constructor(
    private readonly plannedIncomeService: PlannedIncomeService,
    private readonly formStoreService: FormStoreService,
    private readonly directionService: DirectionService,
    private readonly dealService: DealService,
    private readonly documentBillService: DocumentBillService,
    public readonly globalService: GlobalService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly accessService: AccessService
  ) {
    super();
  }

  public ngOnInit() {
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const billId = this.formStoreService.getState.forms[this.index]?.data?.billId;
    const directionId = this.formStoreService.getState.forms[this.index]?.data?.directionId;

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (!isNaN(dealId)) {
      this.dealService.findById<DealFindInterface>(dealId).subscribe((deal) => {
        this.plannedIncome.deal = deal;
        this.plannedIncome.contragent = deal.contragent;
        this.plannedIncome.company = deal.company;
        this.plannedIncome.amount = deal.amount;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(billId)) {
      this.documentBillService.findById<BillFindInterface>(billId).subscribe((bill) => {
        const fieldsToCopy: Array<keyof BillFindInterface> = [
          'direction',
          'unit',
          'contragent',
          'company',
          'amount',
          'deal',
          'filial'
        ];

        this.plannedIncome = <PlannedIncomeFormInterface>{};
        fieldsToCopy.forEach((key) => {
          this.plannedIncome[key] = bill[key];
        });

        this.plannedIncome.bill = bill;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(directionId)) {
      this.directionService
        .findById<DirectionFindInterface>(directionId)
        .subscribe((direction) => {
          this.plannedIncome.direction = direction;
          this.cdRef.markForCheck();
        });
    }

    this.directionService.find<DirectionFindInterface>().subscribe((directions) => {
      this.directions = directions;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.plannedIncomeService
      .findById<PlannedIncomeFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId, <
        PlannedIncomeFindOneQueryInterface
      >{
        select: [
          'id',
          'date',
          'amount',
          'direction',
          'unit',
          'filial',
          'deal',
          'contragent',
          'company',
          'incomesReceived',
          'responsible',
          'userAuditors',
          'userGroupAuditors',
          'bill'
        ]
      })
      .subscribe((plannedIncome) => {
        this.plannedIncome = plannedIncome;
        this.formStoreService.setLoading(false, this.index);
        this.cdRef.markForCheck();
      });
  }

  public savePlannedIncome() {
    const requiredAnalyticalFeatures = this.globalService.globalInfo?.requiredAnalyticalFeatures;
    if (
      (requiredAnalyticalFeatures?.unitsRequired && !this.plannedIncome.unit) ||
      (requiredAnalyticalFeatures?.filialsRequired && !this.plannedIncome.filial)
    ) {
      this.errors = Object.create({});

      if (requiredAnalyticalFeatures?.unitsRequired && !this.plannedIncome.unit) {
        this.errors.unit = 'Бюджетная единица не выбрана' as any;
      }
      if (requiredAnalyticalFeatures?.filialsRequired && !this.plannedIncome.filial) {
        this.errors.filial = 'Филиал не выбран' as any;
      }
      return;
    }

    if (this.checkValidate(this.plannedIncome).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.plannedIncomeService
          .update<PlannedIncomeFormInterface, PlannedIncomeFindInterface>(this.plannedIncome)
          .subscribe({
            next: (plannedIncome) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(
                `Планируемое поступление №${this.formStoreService.getState.forms[this.index]?.entityId} обновлено`
              );
              this.formStoreService.saveNext(plannedIncome, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.plannedIncomeService.create<PlannedIncomeFindInterface>(this.plannedIncome).subscribe({
          next: (plannedIncome) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Планируемое поступление создано');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/payments/planned-income/card/' + plannedIncome.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(plannedIncome, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deletePlannedIncome() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить планируемое поступление №${this.plannedIncome?.['id']}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.plannedIncomeService.deleteById(this.plannedIncome?.['id']).subscribe(() => {
          this.errorService.addSuccessMessage(`Планируемое поступление №${this.plannedIncome['id']} удалено`);
          this.formStoreService.deleteNext(this.index);
        });
      }
    });
  }

  public setIncomesReceived() {
    if (this.plannedIncome.incomesReceived) {
      this.plannedIncomeService
        .setIncomesReceivedForOne(this.formStoreService.getState.forms[this.index]?.entityId)
        .subscribe();
    } else {
      this.plannedIncomeService
        .dropIncomesReceivedForOne(this.formStoreService.getState.forms[this.index]?.entityId)
        .subscribe();
    }
  }

  public changeCompany() {
    this.currencies = [];

    const prevCurrency = this.plannedIncome.currency;
    this.plannedIncome.currency = null;

    if (this.plannedIncome.company?.currencies?.length) {
      this.currencies = this.plannedIncome.company.currencies;
      setTimeout(() => {
        if (prevCurrency?.id) {
          this.plannedIncome.currency = this.currencies.find((currency) => currency.id === prevCurrency.id);
        }
        if (!this.plannedIncome.currency) {
          this.plannedIncome.currency = this.currencies.find((currency) => currency.isDefault);
        }
        this.plannedIncome['rate'] = this.plannedIncome.currency?.latestRate?.rate ?? 1;
        this.cdRef.markForCheck();
      });
    }
  }

  public changeAmount(amount: string | number) {
    this.plannedIncome.amount = Number(amount);
  }
}
